<template>
	<Head title="Verificação de cameras e webcam - Prova"></Head>

	<div class="container">
		<div class="row align-items-center mt-10 mb-10 row-logo">
			<div class="col mx-auto text-center">
				<img
					alt="Logo Habilite-se"
					src="/habil/images/logo.svg"
					class="h-35px" />
			</div>
		</div>

		<div class="row align-items-center row-card">
			<div class="col-sm-8 col-md-8 mx-auto text-center">
				<div class="card">
					<div class="card-body">
						<div class="text-center mb-6">
							<h2 class="m-2">
								Verificando conectividade com as câmeras de
								segurança
							</h2>
							<p style="font-size: 15px; color: #747a7e">
								Aguarde enquanto o sistema verifica as câmeras
								de segurança da sala de prova.
								<br />
								Esta checagem pode levar alguns minutos.
							</p>
						</div>

						<div class="exam-info-panel mb-8">
							<div class="exam-info-item">
								<span class="exam-info-label">Nome</span>
								<strong>{{ props.user_name }}</strong>
							</div>
							<div class="exam-info-item">
								<span class="exam-info-label">CPF</span>
								<strong>{{ props.user_cpf }}</strong>
							</div>
							<div class="exam-info-item exam-info-item-wide">
								<span class="exam-info-label">Computador</span>
								<strong>{{ props.descricao_computer }}</strong>
							</div>
							<div class="exam-info-item exam-info-item-wide">
								<span class="exam-info-label">CFC</span>
								<strong>{{ props.subtenant_name }}</strong>
							</div>
						</div>

						<div class="row mb-5">
							<div class="mt-4" style="text-align: center">
								<div class="verification-status-list">
									<div
										id="computer-check"
										class="verification-status-item">
										<span>Máquina certificada</span>
										<i
											v-if="
												connectionStatus.connection &&
												connectionStatus.verified
											"
											class="fas fa-check-circle fa-1x text-success">
										</i>
										<i
											v-if="connectionSpinner"
											class="fa fa-spin fa-spinner fa-1x"
											style="color: coral">
										</i>
										<i
											v-else-if="
												!connectionStatus.connection ||
												!connectionStatus.verified
											"
											class="fas fa-cancel fa-1x text-danger">
										</i>
									</div>

									<div
										id="webcam"
										class="verification-status-item">
										<span>Webcam</span>
										<i
											v-if="webCamCheck"
											id="webcam-check"
											class="fas fa-check-circle fa-1x text-success"></i>
										<i
											v-if="webCamSpinner"
											id="webcam-spinner"
											class="fa fa-spin fa-spinner fa-1x"
											style="color: coral"></i>
									</div>
								</div>

								<div
									class="camera-channel-list"
									id="progresso-cameras">
									<div
										v-for="camera in cameras"
										:key="camera.channel_id"
										class="camera-channel-item">
										<span
											class="bullet bullet-dot h-15px w-15px"
											:class="{
												'bg-success':
													camera.status_ready,
												'bg-danger':
													camera.status_ready ===
													false,
											}"></span>
										<span
											>Canal {{ camera.channel_id }}</span
										>
									</div>
								</div>
							</div>
						</div>
						<p
							v-if="redirectCountdownStarted"
							id="confirm-button"
							class="fw-bolder fs-3 text-primary">
							Redirecionando você para a prova em
							{{ redirectCountdownSeconds }} segundos.
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { Head, router } from '@inertiajs/vue3';
	import { computed, ref, onMounted, watch, onBeforeUnmount } from 'vue';
	import axios, { AxiosResponse } from 'axios';
	import { CameraChannel } from '../../Interfaces/CameraChannel';
	import { BrowserWebcamStreamingConfig } from '../../Interfaces/BrowserWebcamStreaming';
	import { commonStore } from '../../stores/common';
	import { AxiosError } from 'axios';
	import { checkConnection } from '../../websockets/WebsocketVerification';
	import { ensureExamBrowserWebcamStarted } from '../../utils/examBrowserWebcam';
	import { browserWebcamStreamingStore } from '../../stores/browserWebcamStreaming';

	interface OpenCamWebcamProps {
		mock_camera_channels_webcam_verification: boolean;
		user_name: string;
		user_cpf: string;
		subtenant_name: string;
		start_dvr_url: string;
		start_webcam_url: string;
		logout_url: string;
		final_exam_url: string;
		biometry_url: string;
		state_exam: string;
		go_back_exam: string;
		action_start_webcam: string;
		computer_uid: string;
		enrollment_test_id: string;
		get_enrollment_url: string;
		open_enrollment_test_state_const: number;
		is_ws_verification: boolean;
		descricao_computer: string;
		is_agent_support_ws_verification: boolean;
		browser_webcam_streaming_enabled?: boolean;
		browser_webcam_streaming_config?: BrowserWebcamStreamingConfig | null;
		browser_webcam_continue_url?: string | null;
	}

	interface EnrollmentTestStatus {
		id: number;
		state: number;
		computer?: { webcam_online: boolean } | null;
	}

	const common = commonStore();
	const browserWebcam = browserWebcamStreamingStore();
	const props = withDefaults(defineProps<OpenCamWebcamProps>(), {
		browser_webcam_streaming_enabled: false,
		browser_webcam_streaming_config: null,
		browser_webcam_continue_url: null,
	});

	const camerasOnline = ref(false);
	const webcamOnline = ref(false);
	const browserBackendWebcamOnline = ref(false);

	const cameraSpinner = ref(true);
	const cameraCheck = ref(false);
	const redirectCountdownSeconds = ref(5);
	const redirectCountdownStarted = ref(false);
	let redirectCountdownInterval: NodeJS.Timeout | undefined;

	const webCamSpinner = ref(true);
	const webCamCheck = ref(false);

	const connectionSpinner = ref(true);
	const connectionStatus = ref({
		connection: false,
		verified: false,
	});

	const cameras = ref<Array<CameraChannel>>([]);
	const nextPage: string = props.biometry_url;
	const continueUrl = computed<string>(() => {
		if (
			props.browser_webcam_streaming_enabled &&
			props.browser_webcam_continue_url
		) {
			return props.browser_webcam_continue_url;
		}

		return props.state_exam === 'SCHEDULED' ? nextPage : props.go_back_exam;
	});
	const readyToContinue = computed(
		() =>
			props.mock_camera_channels_webcam_verification ||
			(camerasOnline.value && webcamOnline.value)
	);

	function continueToExam() {
		router.visit(continueUrl.value);
	}

	function startRedirectCountdown() {
		if (redirectCountdownStarted.value) {
			return;
		}

		redirectCountdownStarted.value = true;
		redirectCountdownSeconds.value = 5;

		redirectCountdownInterval = setInterval(() => {
			redirectCountdownSeconds.value = redirectCountdownSeconds.value - 1;

			if (redirectCountdownSeconds.value <= 0) {
				clearRedirectCountdown();
				continueToExam();
			}
		}, 1000);
	}

	function clearRedirectCountdown() {
		if (redirectCountdownInterval) {
			clearInterval(redirectCountdownInterval);
			redirectCountdownInterval = undefined;
		}
	}

	function verifyCameras() {
		axios
			.get(props.start_dvr_url)
			.then((response) => {
				cameraCheck.value = true;
				cameraSpinner.value = false;
				camerasOnline.value = true;
				cameras.value = response.data.camera_channels;
			})
			.catch((response) => {
				cameraCheck.value = false;
				cameraSpinner.value = true;
				camerasOnline.value = false;
				cameras.value = response.response.data.camera_channels;
			});
	}

	function startWebcam() {
		if (props.browser_webcam_streaming_enabled) {
			return;
		}

		axios
			.post(props.action_start_webcam, {
				uid: props.computer_uid,
				enrollment_test_id: props.enrollment_test_id,
			})
			.then((response) => {})
			.catch((error: AxiosResponse) => {});
	}

	function syncBrowserWebcamState() {
		const isPublishing = browserWebcam.isPublishing;
		const isOnline = isPublishing && browserBackendWebcamOnline.value;

		webCamCheck.value = isOnline;
		webCamSpinner.value = !isOnline;
		webcamOnline.value = isOnline;
	}

	function syncBrowserBackendWebcamState(
		enrollmentTest: EnrollmentTestStatus
	) {
		browserBackendWebcamOnline.value = Boolean(
			enrollmentTest.computer?.webcam_online
		);
		syncBrowserWebcamState();
	}

	function verifyWebCam() {
		if (props.browser_webcam_streaming_enabled) {
			syncBrowserWebcamState();

			if (browserWebcam.isPublishing) {
				return;
			}

			ensureExamBrowserWebcamStarted({
				browser_webcam_streaming_enabled:
					props.browser_webcam_streaming_enabled,
				browser_webcam_streaming_config:
					props.browser_webcam_streaming_config,
			})
				.then(syncBrowserWebcamState)
				.catch(() => {
					webcamOnline.value = false;
					webCamCheck.value = false;
					webCamSpinner.value = true;
				});
			return;
		}

		axios
			.get(props.start_webcam_url)
			.then(() => {
				webCamCheck.value = true;
				webCamSpinner.value = false;
				webcamOnline.value = true;
			})
			.catch(() => {
				webcamOnline.value = false;
				webCamCheck.value = false;
				webCamSpinner.value = true;
				startWebcam();
			});
	}

	async function timerCamerasWebcam() {
		await validateEnrollmentTestState();

		verifyCameras();

		verifyWebCam();

		let checkWebcamCameras = setInterval(async () => {
			await validateEnrollmentTestState();

			verifyCameras();

			verifyWebCam();

			if (webcamOnline.value && camerasOnline.value) {
				clearInterval(checkWebcamCameras);
			}
		}, 5 * 1000);
	}

	function timerExam() {
		setTimeout(
			() => {
				browserWebcam.stop('open-cam-timeout');
				window.location.href = props.logout_url;
			},
			60 * 60 * 1000
		);
	}

	async function validateEnrollmentTestState() {
		const enrollmentTest = await getEnrollmentTestStatus(
			props.get_enrollment_url
		);

		if (enrollmentTest === null) {
			return;
		}

		if (enrollmentTest.state !== props.open_enrollment_test_state_const) {
			browserWebcam.stop('invalid-enrollment-state');
			window.location.href = props.logout_url;
			return;
		}

		if (props.browser_webcam_streaming_enabled) {
			syncBrowserBackendWebcamState(enrollmentTest);
		}
	}

	async function getEnrollmentTestStatus(
		url: string
	): Promise<EnrollmentTestStatus | null> {
		let enrollment_test;

		try {
			enrollment_test = await axios
				.get(url)
				.then((response) => response.data.data);
		} catch (err) {
			let error_message = 'Unable to validate enrollment state: ' + err;
			if (err instanceof AxiosError)
				error_message =
					'Unable to validate enrollment state: ' +
					err.response?.data.message;
			console.log(error_message);
		}

		return enrollment_test ?? null;
	}

	onMounted(async () => {
		if (!props.mock_camera_channels_webcam_verification) {
			await timerCamerasWebcam();
		} else {
			// just flag cameras as turned on
			cameraCheck.value = true;
			cameraSpinner.value = false;
			camerasOnline.value = true;

			// just flag webcam as turned on
			webCamCheck.value = true;
			webCamSpinner.value = false;
			webcamOnline.value = true;
		}
		timerExam();

		common.startKeyUp();
	});

	watch(cameras, () => {
		if (cameras.value !== null) {
			cameras.value.sort(
				(a: CameraChannel, b: CameraChannel) =>
					a.channel_id - b.channel_id
			);
		}
	});

	watch(
		() => browserWebcam.status,
		() => {
			if (props.browser_webcam_streaming_enabled) {
				syncBrowserWebcamState();
			}
		}
	);

	watch(
		readyToContinue,
		(isReady) => {
			if (isReady) {
				startRedirectCountdown();
			}
		},
		{ immediate: true }
	);

	onMounted(() => {
		checkConnection(props, connectionStatus, connectionSpinner);

		setInterval(() => {
			checkConnection(props, connectionStatus, connectionSpinner);
		}, 25000);
	});

	onBeforeUnmount(() => {
		clearRedirectCountdown();
	});
</script>

<style scoped>
	.exam-info-panel {
		background: #f5f8fa;
		border: 1px solid #e4e6ef;
		border-radius: 8px;
		display: grid;
		gap: 12px;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		padding: 16px;
		text-align: left;
	}

	.exam-info-item {
		display: grid;
		gap: 2px;
		min-width: 0;
	}

	.exam-info-item-wide {
		grid-column: 1 / -1;
	}

	.exam-info-label {
		color: #7e8299;
		display: block;
		font-size: 13px;
		font-weight: 700;
		line-height: 1;
		margin: 0;
		padding: 0;
		text-transform: uppercase;
	}

	.exam-info-item strong {
		color: #181c32;
		font-size: 16px;
		line-height: 1.35;
		overflow-wrap: anywhere;
	}

	.camera-channel-list {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		justify-content: center;
		margin-top: 12px;
		max-width: 100%;
	}

	.verification-status-list {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		justify-content: center;
		max-width: 100%;
	}

	.verification-status-item,
	.camera-channel-item {
		align-items: center;
		background: #ffffff;
		border: 1px solid #e4e6ef;
		border-radius: 8px;
		color: #3f4254;
		display: inline-flex;
		font-size: 16px;
		font-weight: 700;
		gap: 8px;
		min-height: 44px;
		padding: 8px 12px;
	}

	.verification-status-item {
		font-size: 17px;
		min-height: 48px;
		padding: 10px 14px;
	}

	@media (max-width: 575.98px) {
		.exam-info-panel {
			grid-template-columns: 1fr;
		}
	}
</style>
